(() => {
  const SELECTOR = '[data-faj-header]';
  const CART_REFRESH_EVENT = 'faj:cart:refresh';
  const CART_MUTATION_PATTERN = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:\?|$)/i;

  const dispatchCartRefresh = () => {
    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT));
  };

  const isCartMutationRequest = (input, init = {}) => {
    try {
      const requestUrl =
        typeof input === 'string'
          ? input
          : input instanceof Request
            ? input.url
            : String(input?.url || input || '');

      const method = String(
        init.method ||
        (input instanceof Request ? input.method : '') ||
        'GET'
      ).toUpperCase();

      if (method === 'GET' || method === 'HEAD') return false;

      const url = new URL(requestUrl, window.location.origin);
      return CART_MUTATION_PATTERN.test(url.pathname);
    } catch (error) {
      return false;
    }
  };

  const installCartMutationBridge = () => {
    if (window.__FAJ_HEADER_CART_MUTATION_BRIDGE__) return;
    window.__FAJ_HEADER_CART_MUTATION_BRIDGE__ = true;

    /* Detect fetch-based Shopify AJAX cart mutations. */
    if (typeof window.fetch === 'function') {
      const nativeFetch = window.fetch.bind(window);

      window.fetch = async (...args) => {
        const mutation = isCartMutationRequest(args[0], args[1]);
        const response = await nativeFetch(...args);

        if (mutation && response.ok) {
          window.setTimeout(dispatchCartRefresh, 0);
        }

        return response;
      };
    }

    /* Detect older XMLHttpRequest-based cart mutations too. */
    if (typeof XMLHttpRequest !== 'undefined') {
      const nativeOpen = XMLHttpRequest.prototype.open;
      const nativeSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        this.__fajCartMutation = isCartMutationRequest(url, { method });
        return nativeOpen.call(this, method, url, ...rest);
      };

      XMLHttpRequest.prototype.send = function(...args) {
        if (this.__fajCartMutation) {
          this.addEventListener(
            'load',
            () => {
              if (this.status >= 200 && this.status < 400) {
                window.setTimeout(dispatchCartRefresh, 0);
              }
            },
            { once: true }
          );
        }

        return nativeSend.apply(this, args);
      };
    }
  };

  installCartMutationBridge();

  class FajDynamicHeader {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.predictiveEnabled = root.dataset.predictiveEnabled === 'true';
      this.searchEndpoint = root.dataset.searchEndpoint || '/search/suggest';
      this.searchLimit = root.dataset.searchLimit || '5';
      this.abortController = null;
      this.debounceTimer = null;
      this.lastFocusedElement = null;
      this.currentMobilePanel = 'root';
      this.megaCloseTimer = null;
      this.boundKeydown = this.onDocumentKeydown.bind(this);

      this.cartJsonUrl = root.dataset.cartJsonUrl || '/cart.js';
      this.cartRefreshTimer = null;
      this.cartRefreshController = null;
      this.boundCartRefresh = this.onCartRefreshEvent.bind(this);

      this.stickyEnabled = root.dataset.stickyEnabled === 'true';
      this.stickyBehavior = root.dataset.stickyBehavior || 'always';
      this.stickyWrapper = root.closest('.shopify-section') || root.parentElement;
      this.lastScrollY = Math.max(window.scrollY || 0, 0);
      this.stickyRaf = 0;
      this.stickyHoldUntil = 0;
      this.boundStickyScroll = this.onStickyScroll.bind(this);
      this.boundStickyResize = this.updateStickyMeasurements.bind(this);

      this.init();
    }

    init() {
      this.initSearch();
      this.initMegaMenu();
      this.initDrawer();
      this.initCartCounter();
      this.initCartTrigger();
      this.initStickyHeader();
    }

    initSearch() {
      this.searchWidgets = [...this.root.querySelectorAll('[data-faj-search]')];

      this.searchWidgets.forEach((widget) => {
        const input = widget.querySelector('[data-faj-search-input]');
        const results = widget.querySelector('[data-faj-search-results]');
        if (!input || !results) return;

        input.addEventListener('input', () => {
          window.clearTimeout(this.debounceTimer);
          const query = input.value.trim();
          if (!this.predictiveEnabled || query.length < 2) {
            this.closeSearch(widget);
            return;
          }
          this.debounceTimer = window.setTimeout(() => this.fetchSearch(widget, query), 180);
        });

        input.addEventListener('focus', () => {
          if (results.innerHTML.trim()) this.openSearch(widget);
        });

        input.addEventListener('keydown', (event) => this.onSearchKeydown(event, widget));
      });

      document.addEventListener('pointerdown', (event) => {
        this.searchWidgets.forEach((widget) => {
          if (!widget.contains(event.target)) this.closeSearch(widget);
        });
      });
    }

    async fetchSearch(widget, query) {
      const input = widget.querySelector('[data-faj-search-input]');
      const results = widget.querySelector('[data-faj-search-results]');
      if (!input || !results) return;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      widget.classList.add('is-loading');

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': this.searchLimit,
        'resources[options][unavailable_products]': 'last',
        section_id: this.sectionId
      });

      try {
        const response = await fetch(`${this.searchEndpoint}?${params.toString()}`, {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rendered = doc.querySelector('[data-faj-predictive-results]');
        results.innerHTML = rendered ? rendered.outerHTML : '';

        if (input.value.trim() === query && results.innerHTML.trim()) {
          this.openSearch(widget);
        }
      } catch (error) {
        if (error.name !== 'AbortError') this.closeSearch(widget);
      } finally {
        widget.classList.remove('is-loading');
      }
    }

    openSearch(widget) {
      const input = widget.querySelector('[data-faj-search-input]');
      const results = widget.querySelector('[data-faj-search-results]');
      if (!input || !results) return;

      this.setStickyHidden(false);
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    closeSearch(widget) {
      const input = widget.querySelector('[data-faj-search-input]');
      const results = widget.querySelector('[data-faj-search-results]');
      if (!input || !results) return;
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      results.querySelectorAll('.is-keyboard-active').forEach((item) => item.classList.remove('is-keyboard-active'));
    }

    onSearchKeydown(event, widget) {
      const results = widget.querySelector('[data-faj-search-results]');
      const options = results ? [...results.querySelectorAll('[data-faj-search-option]')] : [];
      if (event.key === 'Escape') {
        this.closeSearch(widget);
        event.currentTarget.blur();
        return;
      }
      if (!options.length || results.hidden) return;

      let index = options.findIndex((option) => option.classList.contains('is-keyboard-active'));
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        index = (index + 1) % options.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        index = index <= 0 ? options.length - 1 : index - 1;
      } else if (event.key === 'Enter' && index >= 0) {
        event.preventDefault();
        options[index].click();
        return;
      } else {
        return;
      }

      options.forEach((option, optionIndex) => {
        const active = optionIndex === index;
        option.classList.toggle('is-keyboard-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      options[index].scrollIntoView({ block: 'nearest' });
    }

    initMegaMenu() {
      this.megaRoot = this.root.querySelector('[data-faj-mega-root]');
      this.megaTrigger = this.root.querySelector('[data-faj-mega-trigger]');
      this.megaMenu = this.root.querySelector('[data-faj-mega-menu]');
      if (!this.megaRoot || !this.megaTrigger || !this.megaMenu) return;

      const open = () => {
        window.clearTimeout(this.megaCloseTimer);
        this.setStickyHidden(false);
        this.megaMenu.hidden = false;
        this.megaTrigger.setAttribute('aria-expanded', 'true');
      };
      const closeSoon = () => {
        window.clearTimeout(this.megaCloseTimer);
        this.megaCloseTimer = window.setTimeout(() => this.closeMegaMenu(), 130);
      };

      this.megaRoot.addEventListener('pointerenter', open);
      this.megaRoot.addEventListener('pointerleave', closeSoon);
      this.megaRoot.addEventListener('focusin', open);
      this.megaRoot.addEventListener('focusout', (event) => {
        if (!this.megaRoot.contains(event.relatedTarget)) closeSoon();
      });
      this.megaTrigger.addEventListener('click', () => {
        if (this.megaMenu.hidden) open();
        else this.closeMegaMenu();
      });

      this.megaMenu.querySelectorAll('[data-faj-mega-tab]').forEach((tab) => {
        const activate = () => this.activateMegaTab(tab.dataset.fajMegaTab);
        tab.addEventListener('pointerenter', activate);
        tab.addEventListener('focus', activate);
      });
    }

    activateMegaTab(index) {
      if (!this.megaMenu) return;
      this.megaMenu.querySelectorAll('[data-faj-mega-tab]').forEach((tab) => {
        const active = tab.dataset.fajMegaTab === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.megaMenu.querySelectorAll('[data-faj-mega-panel]').forEach((panel) => {
        const active = panel.dataset.fajMegaPanel === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;

        if (active) {
          const scrollArea = panel.querySelector('[data-faj-mega-panel-scroll]');
          if (scrollArea) scrollArea.scrollTop = 0;
        }
      });
    }

    closeMegaMenu() {
      if (!this.megaMenu || !this.megaTrigger) return;
      this.megaMenu.hidden = true;
      this.megaTrigger.setAttribute('aria-expanded', 'false');
    }

    initDrawer() {
      this.drawerLayer = this.root.querySelector('[data-faj-drawer-layer]');
      this.drawer = this.root.querySelector('[data-faj-drawer]');
      this.drawerTitle = this.root.querySelector('[data-faj-drawer-title]');
      this.drawerBack = this.root.querySelector('[data-faj-panel-back]');
      this.drawerBrand = this.root.querySelector('[data-faj-drawer-brand]');
      this.drawerSearch = this.root.querySelector('[data-faj-drawer-search]');
      this.menuOpen = this.root.querySelector('[data-faj-menu-open]');
      if (!this.drawerLayer || !this.drawer || !this.menuOpen) return;

      this.menuOpen.addEventListener('click', () => this.openDrawer());
      this.root.querySelectorAll('[data-faj-menu-close]').forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
      if (this.drawerBack) this.drawerBack.addEventListener('click', () => this.goBackPanel());

      this.drawer.querySelectorAll('[data-faj-panel-target]').forEach((button) => {
        button.addEventListener('click', () => this.showMobilePanel(button.dataset.fajPanelTarget));
      });

      this.drawer.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (link && !link.closest('[data-faj-search-results]')) this.closeDrawer();
      });
    }

    openDrawer() {
      this.lastFocusedElement = document.activeElement;
      this.setStickyHidden(false);
      this.drawerLayer.hidden = false;
      requestAnimationFrame(() => this.drawerLayer.classList.add('is-open'));
      this.drawer.setAttribute('aria-hidden', 'false');
      this.menuOpen.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('faj-drawer-open');
      document.addEventListener('keydown', this.boundKeydown);
      this.showMobilePanel('root', false);
      const closeButton = this.drawer.querySelector('[data-faj-menu-close]');
      if (closeButton) window.setTimeout(() => closeButton.focus(), 50);
    }

    closeDrawer() {
      if (!this.drawerLayer || this.drawerLayer.hidden) return;
      this.drawerLayer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.menuOpen.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('faj-drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);
      window.setTimeout(() => { this.drawerLayer.hidden = true; }, 270);
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') this.lastFocusedElement.focus();
    }

    showMobilePanel(id, focusBack = true) {
      const target = this.drawer.querySelector(`[data-faj-mobile-panel="${CSS.escape(id)}"]`);
      if (!target) return;
      this.currentMobilePanel = id;
      this.drawer.querySelectorAll('[data-faj-mobile-panel]').forEach((panel) => {
        const active = panel === target;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      const root = id === 'root';
      if (this.drawerTitle) this.drawerTitle.textContent = target.dataset.title || '';
      if (this.drawerBack) this.drawerBack.hidden = root;
      if (this.drawerTitle) this.drawerTitle.hidden = !root;
      if (this.drawerBrand) this.drawerBrand.hidden = !root;
      if (this.drawerSearch) this.drawerSearch.hidden = !root;
      const body = this.root.querySelector('[data-faj-drawer-body]');
      if (body) body.scrollTop = 0;
      if (!root && focusBack && this.drawerBack) this.drawerBack.focus();
    }

    goBackPanel() {
      const panel = this.drawer.querySelector(`[data-faj-mobile-panel="${CSS.escape(this.currentMobilePanel)}"]`);
      this.showMobilePanel(panel?.dataset.parent || 'root', false);
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closeMegaMenu();
        this.closeDrawer();
        return;
      }
      if (event.key !== 'Tab' || !this.drawerLayer?.classList.contains('is-open')) return;
      const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    initCartCounter() {
      this.cartCountNodes = [
        ...this.root.querySelectorAll('[data-faj-cart-count]')
      ];

      if (!this.cartCountNodes.length) return;

      document.addEventListener(CART_REFRESH_EVENT, this.boundCartRefresh);

      [
        'cart:updated',
        'cart:refresh',
        'cart:change',
        'cart-drawer:updated',
        'product:added',
        'ajaxProduct:added',
        'theme:cart:change',
        'faj:cart:updated'
      ].forEach((eventName) => {
        document.addEventListener(eventName, this.boundCartRefresh);
      });

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.scheduleCartRefresh(0);
      });
    }

    onCartRefreshEvent(event) {
      this.keepStickyHeaderVisible(1200);

      const detail = event?.detail || {};
      const possibleCount =
        detail.item_count ??
        detail.itemCount ??
        detail.cart?.item_count ??
        detail.cart?.itemCount;

      if (Number.isFinite(Number(possibleCount))) {
        this.setCartCount(Number(possibleCount));
        return;
      }

      this.scheduleCartRefresh();
    }

    scheduleCartRefresh(delay = 80) {
      window.clearTimeout(this.cartRefreshTimer);
      this.cartRefreshTimer = window.setTimeout(
        () => this.refreshCartCount(),
        delay
      );
    }

    async refreshCartCount() {
      if (this.cartRefreshController) {
        this.cartRefreshController.abort();
      }

      this.cartRefreshController = new AbortController();

      try {
        const response = await fetch(this.cartJsonUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: this.cartRefreshController.signal
        });

        if (!response.ok) {
          throw new Error(`Cart refresh failed: ${response.status}`);
        }

        const cart = await response.json();
        this.setCartCount(Number(cart.item_count || 0));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('FAJ header cart count could not refresh.', error);
        }
      }
    }

    setCartCount(count) {
      const safeCount = Math.max(0, Number.isFinite(count) ? count : 0);

      this.cartCountNodes.forEach((node) => {
        node.textContent = String(safeCount);
        node.hidden = safeCount === 0;

        if (safeCount > 0) {
          node.classList.remove('is-updating');
          void node.offsetWidth;
          node.classList.add('is-updating');

          window.setTimeout(() => {
            node.classList.remove('is-updating');
          }, 180);
        }
      });

      this.root.dataset.cartCount = String(safeCount);
    }

    initCartTrigger() {
      const trigger = this.root.querySelector('[data-faj-cart-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', () => {
        const event = new CustomEvent('faj:cart:open', {
          bubbles: true,
          cancelable: true
        });

        const shouldContinue = trigger.dispatchEvent(event);

        if (shouldContinue) {
          window.location.assign(trigger.dataset.cartUrl || '/cart');
        }
      });
    }

    initStickyHeader() {
      if (!this.stickyEnabled || !this.stickyWrapper) return;

      this.updateStickyMeasurements();

      if (this.stickyBehavior !== 'scroll_up') {
        this.setStickyHidden(false);
        return;
      }

      this.root.dataset.stickyBehavior = 'scroll_up';
      window.addEventListener('scroll', this.boundStickyScroll, {
        passive: true
      });
      window.addEventListener('resize', this.boundStickyResize, {
        passive: true
      });

      this.onStickyScroll();
    }

    updateStickyMeasurements() {
      if (!this.stickyWrapper) return;

      const height = Math.ceil(this.root.getBoundingClientRect().height);
      this.stickyWrapper.style.setProperty(
        '--faj-header-hide-distance',
        `${Math.max(height, 1)}px`
      );
    }

    stickyInteractionOpen() {
      const megaOpen = Boolean(this.megaMenu && !this.megaMenu.hidden);
      const drawerOpen = Boolean(
        this.drawerLayer && this.drawerLayer.classList.contains('is-open')
      );
      const searchOpen = Boolean(
        this.searchWidgets?.some((widget) => {
          const results = widget.querySelector('[data-faj-search-results]');
          return results && !results.hidden;
        })
      );

      return megaOpen || drawerOpen || searchOpen;
    }

    keepStickyHeaderVisible(duration = 0) {
      if (!this.stickyEnabled || this.stickyBehavior !== 'scroll_up') return;

      this.stickyHoldUntil = Math.max(
        this.stickyHoldUntil,
        Date.now() + Math.max(Number(duration) || 0, 0)
      );
      this.lastScrollY = Math.max(window.scrollY || 0, 0);
      this.setStickyHidden(false);
    }

    onStickyScroll() {
      if (
        !this.stickyEnabled ||
        this.stickyBehavior !== 'scroll_up' ||
        this.stickyRaf
      ) {
        return;
      }

      this.stickyRaf = window.requestAnimationFrame(() => {
        this.stickyRaf = 0;

        const currentY = Math.max(window.scrollY || 0, 0);
        const delta = currentY - this.lastScrollY;
        const headerHeight = Math.max(this.root.offsetHeight, 1);

        if (Date.now() < this.stickyHoldUntil) {
          this.setStickyHidden(false);
          this.lastScrollY = currentY;
          return;
        }

        if (currentY <= 8 || this.stickyInteractionOpen()) {
          this.setStickyHidden(false);
          this.lastScrollY = currentY;
          return;
        }

        if (Math.abs(delta) < 5) {
          return;
        }

        if (delta > 0 && currentY > headerHeight + 20) {
          this.setStickyHidden(true);
        } else if (delta < 0) {
          this.setStickyHidden(false);
        }

        this.lastScrollY = currentY;
      });
    }

    setStickyHidden(hidden) {
      if (!this.stickyWrapper || this.stickyBehavior !== 'scroll_up') return;

      const shouldHide = Boolean(hidden);
      this.stickyWrapper.classList.toggle(
        'faj-header-wrapper--hidden',
        shouldHide
      );
      this.root.classList.toggle('is-scroll-revealed', !shouldHide);
      this.root.dataset.stickyState = shouldHide ? 'hidden' : 'visible';
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.fajInitialized === 'true') return;
      root.dataset.fajInitialized = 'true';
      new FajDynamicHeader(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
